import { BulletListContent, FlexBox, H3, Icon, Ul } from '@vp/swan';
import { useIsFeatureFlagActive } from '@99designs/experiments';
import { __ } from '@99designs/i18n';
import { ServiceDetailsPageDataQuery } from '../../graphql';

export type InclusionsProps = {
    title: string;
    inclusions: ServiceDetailsPageDataQuery['serviceDetailPage']['inclusions'];
    properties: ServiceDetailsPageDataQuery['expertServices'][0]['serviceProduct']['properties'];
};

export function Inclusions(props: InclusionsProps) {
    const isFeatureFlagActive = useIsFeatureFlagActive('design_services_sdp_inclusions');

    // Check if required properties exist
    const requiredProperties = ['REVISION_COUNT', 'TURNAROUND_TIME', 'PROJECT_CLOSURE_TIMEFRAME'];
    const hasAllRequiredProperties =
        isFeatureFlagActive() &&
        requiredProperties.every((requiredProp) =>
            props.properties.some((prop) => prop.property === requiredProp)
        );

    const getPropertyCopy = (property: string, value: string) => {
        switch (property) {
            case 'REVISION_COUNT': {
                const revisionProp = props.properties.find(
                    (p) => p.property === 'PROJECT_CLOSURE_TIMEFRAME'
                );
                const days = revisionProp?.value || '30';
                return __(
                    '{{count}} revisions for you to use within {{days}} days to perfect your design',
                    {
                        count: value,
                        days,
                    }
                );
            }
            case 'TURNAROUND_TIME':
                return __('{{days}}-day turnaround time (excludes weekends)', { days: value });
            case 'DESIGNER_SELECTION':
                return value === 'true'
                    ? __(
                          'Pick a Vista-vetted freelance designer to create a completely custom design'
                      )
                    : __('A designer will be assigned to you');
            case 'PRINT_COST_ADDITIONAL':
                return value === 'true'
                    ? __('Print cost is additional')
                    : __('Print cost is included');
            case 'DESIGN_LIVE':
                return value === 'true'
                    ? __('Schedule a live video call')
                    : __('Work with a designer via chat and email');
            case 'LOGO_DESIGN_INCLUDED':
                return value === 'true'
                    ? __('Logo design included')
                    : __('Logo design not included');
            case 'DESIGN_FILES_ACCESS':
                return value === 'true'
                    ? __('Full access to design files')
                    : __('Limited access to design files');
            case 'OWNERSHIP_GUARANTEE':
                return value === 'true'
                    ? __('Ownership guarantee included')
                    : __('Ownership guarantee not included');
            case 'PRINT_FILE_DELIVERED':
                return value === 'true'
                    ? __('Print-ready files delivered')
                    : __('Print-ready files not delivered');
            default:
                return __('{{property}}: {{value}}', { property, value });
        }
    };

    const getPropertyIcon = (property: string, value: string) => {
        switch (property) {
            case 'REVISION_COUNT':
                return <Icon iconType="edit" size="16p" />;
            case 'TURNAROUND_TIME':
                return <Icon iconType="clock" size="16p" />;
            case 'DESIGNER_SELECTION':
                return value === 'true' ? (
                    <Icon iconType="consumer" size="16p" />
                ) : (
                    <Icon iconType="account" size="16p" />
                );
            case 'DESIGN_LIVE':
                return value === 'true' ? (
                    <Icon iconType="videoChat" size="16p" />
                ) : (
                    <Icon iconType="chatReview" size="16p" />
                );
            case 'PRINT_COST_ADDITIONAL':
                return <Icon iconType="priceTag" size="16p" />;
            default:
                return value === 'true' ? (
                    <Icon skin="success" iconType="check" size="16p" />
                ) : (
                    <Icon skin="error" iconType="close" size="16p" />
                );
        }
    };

    return (
        <div>
            <H3 fontSkin="title-subsection" id="inclusions-list">
                {props.title}
            </H3>
            {hasAllRequiredProperties ? (
                <Ul aria-labelledby="inclusions-list" skin="minimal">
                    {props.properties
                        .filter((prop) => prop.property !== 'PROJECT_CLOSURE_TIMEFRAME')
                        .map((prop, i) => {
                            return (
                                <li key={i}>
                                    <BulletListContent>
                                        <FlexBox gap={3} alignItems="flex-start">
                                            <div style={{ minWidth: '16px', flexShrink: 0 }}>
                                                {getPropertyIcon(prop.property, prop.value)}
                                            </div>
                                            <div>{getPropertyCopy(prop.property, prop.value)}</div>
                                        </FlexBox>
                                    </BulletListContent>
                                </li>
                            );
                        })}
                </Ul>
            ) : (
                <Ul aria-labelledby="inclusions-list" skin="checkmark">
                    {props.inclusions.map((inclusion) => (
                        <li>
                            <BulletListContent>{inclusion.description}</BulletListContent>
                        </li>
                    ))}
                </Ul>
            )}
        </div>
    );
}
